/**
 * All Transactions Panel — unified view of every bank transaction across all
 * source documents with programmatic deduplication, running balance, account
 * column, and filters.
 *
 * The Documents tab remains unchanged (per-document, no dedup). This panel
 * provides the consolidated cross-document view with duplicate detection.
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  computeVouch,
  type BankVouchInput,
  type VouchStatus,
  type LedgerCharacterisation,
} from "@cvx/jobs/summarization/agents/bankTransactions/vouchCore";
import {
  computeRunningBalances,
  type BalanceGap,
} from "@cvx/jobs/summarization/agents/bankTransactions/balanceCore";
import {
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
} from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/ui/composed/overlays/tooltip";
import { cn } from "@/utils/misc";
import { TransactionDetailSheet } from "./TransactionDetailSheet";
import { TypeBadge, CounterpartyBadge, VouchBadge } from "./badges";
import { TYPE_LABELS } from "./badges.helpers";
import { ColumnsMenu } from "./ColumnsMenu";
import type { ColumnOption } from "./ColumnsMenu";
import { useHiddenColumns } from "./ColumnsMenu.helpers";
import { useAllBankTransactions } from "./useAllBankTransactions";
import { useVouchLedger } from "./useVouchLedger";
import { VouchLedgerPicker } from "./VouchLedgerPicker";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BankTxnEntry {
  _id: string;
  _creationTime: number;
  date: string;
  amount: number;
  direction: "debit" | "credit";
  description: string;
  counterpartyName?: string;
  extractionCounterpartyName?: string;
  reference?: string;
  balanceAfter?: number;
  accountInstitution?: string;
  accountNumber?: string;
  transactionType: string;
  extractionTransactionType?: string;
  typeSource: string;
  sourceDocumentId?: string;
  sourceFileName?: string;
  sourceDocumentName?: string;
  notes?: string;
  isManualOverride?: boolean;
  fingerprint?: string;
  isDuplicate?: boolean;
  duplicateOfId?: string;
  vouchStatus?: VouchStatus;
  ledgerCharacterisation?: LedgerCharacterisation;
  vouchOverride?: {
    action: "force_vouched" | "force_unmatched";
    reason?: string;
  };
  /** Computed chronological running balance (MOR-754); null for duplicates. */
  runningBalance?: number | null;
  /** Present when the statement's stated balance breaks against the computed one. */
  balanceGap?: BalanceGap;
  changeHistory: Array<{
    timestamp: number;
    actor: string;
    action: string;
    reason: string;
    previousValues?: Record<string, unknown>;
  }>;
}

interface AllTransactionsPanelProps {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isViewer?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 100;

type GroupByKey =
  "none" | "direction" | "counterparty" | "category" | "account";

const GROUP_BY_OPTIONS: Array<{ value: GroupByKey; label: string }> = [
  { value: "none", label: "No grouping" },
  { value: "direction", label: "Direction" },
  { value: "counterparty", label: "Counterparty" },
  { value: "category", label: "Category" },
  { value: "account", label: "Account" },
];

/** Shared toolbar control sizing — keeps every filter element visually aligned. */
const TOOLBAR_INPUT =
  "h-8 text-xs rounded-lg border bg-background placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";

/**
 * Toggleable columns for the flat transactions table. The trailing
 * duplicate/expand action column and the conditional Vouch column are always
 * managed by the table itself and aren't listed here.
 */
const TXN_COLUMNS: ColumnOption[] = [
  { key: "date", label: "Date" },
  { key: "account", label: "Account" },
  { key: "description", label: "Description" },
  { key: "counterparty", label: "Counterparty" },
  { key: "category", label: "Category" },
  { key: "debit", label: "Debit" },
  { key: "credit", label: "Credit" },
  { key: "balance", label: "Balance" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toSortableDate(date: string): string {
  if (!date) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
    const [d, m, y] = date.split("/");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const match = date.match(/^(\d{1,2})\s+(\w{3,})\s+(\d{4})$/);
  if (match) {
    const months: Record<string, string> = {
      jan: "01",
      feb: "02",
      mar: "03",
      apr: "04",
      may: "05",
      jun: "06",
      jul: "07",
      aug: "08",
      sep: "09",
      oct: "10",
      nov: "11",
      dec: "12",
    };
    const mKey = match[2].toLowerCase().slice(0, 3);
    const mNum = months[mKey] ?? "01";
    return `${match[3]}-${mNum}-${match[1].padStart(2, "0")}`;
  }
  return date;
}

function formatDate(date: string): string {
  if (!date) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-");
    return `${d}/${m}/${y}`;
  }
  return date;
}

const fmtCurrency = (n: number): string =>
  "$" +
  Math.abs(n).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtSignedCurrency = (n: number): string =>
  `${n >= 0 ? "+" : "-"}${fmtCurrency(n)}`;

const fmtBalance = (n: number): string =>
  `${n >= 0 ? "" : "-"}${fmtCurrency(n)}`;

/** Friendly label for a normalised `institution:accountNumber` key. */
function accountKeyLabel(key: string): string {
  if (!key || key === "__default__") return "Unknown account";
  const idx = key.indexOf(":");
  return (
    formatAccountShort(
      key.slice(0, idx) || undefined,
      key.slice(idx + 1) || undefined,
    ) || "Unknown account"
  );
}

function normalizeAccountNumber(value: string | undefined): string {
  if (!value) return "";
  const stripped = value.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
  if (!stripped) return "";
  if (/^\d+$/.test(stripped) && stripped.length >= 12) {
    return stripped.slice(6);
  }
  return stripped;
}

const INSTITUTION_ALIASES: Record<string, string> = {
  cba: "commonwealth bank",
  commbank: "commonwealth bank",
  "commonwealth bank of australia": "commonwealth bank",
  "comm bank": "commonwealth bank",
  nab: "national australia bank",
  "national australia bank limited": "national australia bank",
  anz: "anz",
  "anz banking group": "anz",
  "australia and new zealand banking group": "anz",
  westpac: "westpac",
  "westpac banking corporation": "westpac",
  "st george": "st george",
  "st george bank": "st george",
  "bank of queensland": "bank of queensland",
  boq: "bank of queensland",
  "macquarie bank": "macquarie bank",
  macquarie: "macquarie bank",
  "bendigo bank": "bendigo and adelaide bank",
  "bendigo and adelaide bank": "bendigo and adelaide bank",
  "adelaide bank": "bendigo and adelaide bank",
  amex: "american express",
  "american express": "american express",
};

function normalizeInstitution(value: string | undefined): string {
  if (!value) return "";
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9&\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return INSTITUTION_ALIASES[cleaned] ?? cleaned;
}

/** Builds a canonical key for grouping accounts across documents. */
function accountKey(institution?: string, accountNumber?: string): string {
  return `${normalizeInstitution(institution)}:${normalizeAccountNumber(accountNumber)}`;
}

function formatAccountShort(
  institution?: string,
  accountNumber?: string,
): string {
  const parts: string[] = [];
  if (institution) {
    const abbr =
      institution.length > 20
        ? institution
            .split(/\s+/)
            .map((w) => w[0])
            .join("")
            .toUpperCase()
        : institution;
    parts.push(abbr);
  }
  if (accountNumber) {
    const norm = accountNumber.replace(/\s+/g, "");
    parts.push(norm.length > 6 ? `...${norm.slice(-4)}` : norm);
  }
  return parts.join(" — ") || "Unknown";
}

function formatAccountFull(
  institution?: string,
  accountNumber?: string,
  accountName?: string,
): string {
  const parts: string[] = [];
  if (institution) parts.push(institution);
  if (accountName) parts.push(accountName);
  if (accountNumber) parts.push(accountNumber);
  return parts.join(" — ") || "Unknown account";
}

type SortField = "date" | "amount" | "account" | null;
type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AllTransactionsPanel({
  jobId,
  taskId,
  isViewer,
}: AllTransactionsPanelProps) {
  const {
    transactions: rawTransactions,
    isLoading,
    isLoadingAll,
    loadedCount,
  } = useAllBankTransactions(jobId);

  // Vouching (MOR-574): the ledger is a separately-chosen library document,
  // parsed client-side. Match its rows against the bank transactions here.
  const {
    configured: ledgerConfigured,
    isParsing: ledgerParsing,
    ledgerRows,
    perDoc: ledgerPerDoc,
    ledgerDocIds,
  } = useVouchLedger(jobId, taskId);

  const { data: rawCanonicalAccounts } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getCanonicalBankAccounts, {
      taskId,
    }),
  );

  const baseTransactions = useMemo(
    () => (rawTransactions ?? []) as BankTxnEntry[],
    [rawTransactions],
  );
  const canonicalAccounts = useMemo(
    () =>
      (rawCanonicalAccounts ?? []) as Array<{
        institution?: string;
        accountNumber?: string;
        accountName?: string;
        transactionCount: number;
      }>,
    [rawCanonicalAccounts],
  );

  // Vouching is "active" once a ledger is chosen AND parsed to >=1 row.
  const vouchingActive = ledgerConfigured && ledgerRows.length > 0;

  const vouch = useMemo(() => {
    if (!vouchingActive) return null;
    const bankInput: BankVouchInput[] = baseTransactions.map((t) => ({
      _id: t._id as Id<"bankTransactionEntries">,
      date: t.date,
      amount: t.amount,
      isDuplicate: t.isDuplicate,
      vouchOverride: t.vouchOverride,
    }));
    return computeVouch(bankInput, ledgerRows);
  }, [vouchingActive, baseTransactions, ledgerRows]);

  // Running balance + statement reconciliation (MOR-754). Computed ONCE over
  // every row in date-ascending order — independent of the user's filters and
  // sort — seeded from each statement's own stated balance (so a non-zero
  // opening balance no longer skews the totals) and reconciled against the
  // stated balance at each date boundary. A confirmed break means statements
  // are missing; those surface as `gaps` in the banner and on the rows.
  const balanceComputation = useMemo(() => {
    const ordered = [...baseTransactions].sort((a, b) =>
      toSortableDate(a.date).localeCompare(toSortableDate(b.date)),
    );
    return computeRunningBalances(
      ordered.map((t) => ({
        id: t._id,
        sortableDate: toSortableDate(t.date),
        amount: t.amount,
        direction: t.direction,
        balanceAfter: t.balanceAfter,
        isDuplicate: t.isDuplicate,
        accountKey:
          accountKey(t.accountInstitution, t.accountNumber) || "__default__",
      })),
    );
  }, [baseTransactions]);

  // Merge the computed vouch status + running balance onto each row for the
  // table, grouped view, and detail sheet.
  const transactions = useMemo<BankTxnEntry[]>(() => {
    return baseTransactions.map((t) => {
      const vr = vouch?.byEntryId.get(t._id as Id<"bankTransactionEntries">);
      const bal = balanceComputation.byId.get(t._id);
      return {
        ...t,
        vouchStatus: vr?.vouchStatus,
        ledgerCharacterisation: vr?.ledgerCharacterisation,
        runningBalance: bal?.runningBalance ?? null,
        balanceGap: bal?.gap,
      };
    });
  }, [baseTransactions, vouch, balanceComputation]);

  const vouchSummary = vouch?.summary ?? {
    vouched: 0,
    bankOnly: 0,
    ledgerOnly: 0,
  };

  // Filter state — multi-select filters use arrays of selected values; an
  // empty array means "no filter applied" (matches everything).
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [directionFilter, setDirectionFilter] = useState<
    "all" | "credit" | "debit"
  >("all");
  const [accountFilter, setAccountFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [counterpartyFilter, setCounterpartyFilter] = useState<string[]>([]);
  const [documentFilter, setDocumentFilter] = useState<string[]>([]);
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [hideDuplicates, setHideDuplicates] = useState(true);
  const [vouchFilter, setVouchFilter] = useState<"all" | VouchStatus>("all");
  const [vouchModalOpen, setVouchModalOpen] = useState(false);

  // Sort state
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Grouping
  const [groupBy, setGroupBy] = useState<GroupByKey>("none");

  // Column visibility (persisted per table in localStorage)
  const [hiddenCols, toggleCol] = useHiddenColumns(
    "bankTxn.allTransactions.hiddenColumns",
  );

  // Pagination
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Expanded duplicate detail
  const [expandedDupId, setExpandedDupId] = useState<string | null>(null);

  // Selected transaction for detail sheet
  const [selectedTxn, setSelectedTxn] = useState<BankTxnEntry | null>(null);

  // Build account list from canonical accounts (server-side deduped)
  const accountOptions = useMemo(() => {
    return canonicalAccounts.map((acct) => ({
      value: accountKey(acct.institution, acct.accountNumber),
      label: formatAccountShort(acct.institution, acct.accountNumber),
      fullLabel: formatAccountFull(
        acct.institution,
        acct.accountNumber,
        acct.accountName,
      ),
    }));
  }, [canonicalAccounts]);

  // Build unique category list for filter dropdown
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const txn of transactions) {
      if (txn.transactionType) set.add(txn.transactionType);
    }
    return Array.from(set)
      .sort()
      .map((cat) => ({ value: cat, label: TYPE_LABELS[cat] ?? cat }));
  }, [transactions]);

  // Build unique counterparty list for filter dropdown
  const counterpartyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const txn of transactions) {
      const name = (
        txn.counterpartyName ||
        txn.extractionCounterpartyName ||
        ""
      ).trim();
      if (name) set.add(name);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name, title: name }));
  }, [transactions]);

  // Build unique document list — uses sourceDocumentId as the value (stable
  // even if the friendly name changes) and the friendly name as the label.
  const documentOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const txn of transactions) {
      if (!txn.sourceDocumentId) continue;
      const label =
        txn.sourceDocumentName ?? txn.sourceFileName ?? "Unknown document";
      if (!map.has(txn.sourceDocumentId)) map.set(txn.sourceDocumentId, label);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, label]) => ({ value: id, label, title: label }));
  }, [transactions]);

  // Apply filters
  const filtered = useMemo(() => {
    const predicates: Array<(txn: BankTxnEntry) => boolean> = [];

    if (hideDuplicates) {
      predicates.push((txn) => !txn.isDuplicate);
    }

    if (search) {
      const q = search.toLowerCase();
      predicates.push(
        (txn) =>
          txn.description.toLowerCase().includes(q) ||
          (txn.counterpartyName ?? "").toLowerCase().includes(q) ||
          (txn.extractionCounterpartyName ?? "").toLowerCase().includes(q) ||
          (txn.reference ?? "").toLowerCase().includes(q) ||
          String(txn.amount).includes(q) ||
          (txn.accountInstitution ?? "").toLowerCase().includes(q) ||
          (txn.accountNumber ?? "").toLowerCase().includes(q),
      );
    }

    if (dateFrom) {
      predicates.push((txn) => toSortableDate(txn.date) >= dateFrom);
    }
    if (dateTo) {
      predicates.push((txn) => toSortableDate(txn.date) <= dateTo);
    }

    if (directionFilter !== "all") {
      predicates.push((txn) => txn.direction === directionFilter);
    }

    if (accountFilter.length > 0) {
      const set = new Set(accountFilter);
      predicates.push((txn) =>
        set.has(accountKey(txn.accountInstitution, txn.accountNumber)),
      );
    }

    if (categoryFilter.length > 0) {
      const set = new Set(categoryFilter);
      predicates.push((txn) => set.has(txn.transactionType));
    }

    if (counterpartyFilter.length > 0) {
      const set = new Set(counterpartyFilter);
      predicates.push((txn) =>
        set.has(
          (txn.counterpartyName || txn.extractionCounterpartyName || "").trim(),
        ),
      );
    }

    if (documentFilter.length > 0) {
      const set = new Set(documentFilter);
      predicates.push(
        (txn) => !!txn.sourceDocumentId && set.has(txn.sourceDocumentId),
      );
    }

    if (vouchingActive && vouchFilter !== "all") {
      predicates.push((txn) => txn.vouchStatus === vouchFilter);
    }

    const minVal = amountMin ? parseFloat(amountMin) : null;
    const maxVal = amountMax ? parseFloat(amountMax) : null;
    if (minVal != null && !isNaN(minVal)) {
      predicates.push((txn) => txn.amount >= minVal);
    }
    if (maxVal != null && !isNaN(maxVal)) {
      predicates.push((txn) => txn.amount <= maxVal);
    }

    return predicates.reduce(
      (result, predicate) => result.filter(predicate),
      transactions,
    );
  }, [
    transactions,
    search,
    dateFrom,
    dateTo,
    directionFilter,
    accountFilter,
    categoryFilter,
    counterpartyFilter,
    documentFilter,
    amountMin,
    amountMax,
    hideDuplicates,
    vouchingActive,
    vouchFilter,
  ]);

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (!sortField) return arr;

    arr.sort((a, b) => {
      const cmp = ((): number => {
        switch (sortField) {
          case "date":
            return toSortableDate(a.date).localeCompare(toSortableDate(b.date));
          case "amount":
            return a.amount - b.amount;
          case "account":
            return (a.accountInstitution ?? "").localeCompare(
              b.accountInstitution ?? "",
            );
          default:
            return 0;
        }
      })();
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  // Grouping — compute group buckets from the sorted+filtered list. Each
  // group keeps its own credits/debits totals + chronological list for the
  // expanded view. Sorted by descending count so the densest groups surface
  // first, mirroring the Documents panel behaviour.
  const groups = useMemo(() => {
    if (groupBy === "none") return null;
    const map = new Map<string, BankTxnEntry[]>();
    for (const txn of filtered) {
      const key = getGroupKey(txn, groupBy);
      const arr = map.get(key) ?? [];
      arr.push(txn);
      map.set(key, arr);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([key, txns]) => {
        const { credits, debits } = txns.reduce(
          (totals, t) => {
            if (t.isDuplicate) return totals;
            if (t.direction === "credit") totals.credits += t.amount;
            else totals.debits += t.amount;
            return totals;
          },
          { credits: 0, debits: 0 },
        );
        return {
          key,
          label: getGroupLabel(key, groupBy),
          txns,
          totalCredits: credits,
          totalDebits: debits,
        };
      });
  }, [filtered, groupBy]);

  // Summary stats (from non-duplicate entries)
  const stats = useMemo(() => {
    const nonDup = transactions.filter((t) => !t.isDuplicate);
    const duplicateCount = transactions.filter((t) => t.isDuplicate).length;
    const { totalDebits, totalCredits } = nonDup.reduce(
      (totals, t) => {
        if (t.direction === "debit") totals.totalDebits += t.amount;
        else totals.totalCredits += t.amount;
        return totals;
      },
      { totalDebits: 0, totalCredits: 0 },
    );
    return {
      uniqueCount: nonDup.length,
      duplicateCount,
      totalDebits,
      totalCredits,
      netPosition: totalCredits - totalDebits,
    };
  }, [transactions]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;
  const hasFilters =
    search ||
    dateFrom ||
    dateTo ||
    directionFilter !== "all" ||
    accountFilter.length > 0 ||
    categoryFilter.length > 0 ||
    counterpartyFilter.length > 0 ||
    documentFilter.length > 0 ||
    amountMin ||
    amountMax ||
    vouchFilter !== "all";

  if (isLoading || (transactions.length === 0 && isLoadingAll)) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading all transactions...</span>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <p className="text-sm text-muted-foreground">No transactions found.</p>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Header */}
        <div className="px-5 pb-4 pt-5">
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Unique Transactions
          </p>
          <p className="text-2xl font-bold tabular-nums text-foreground">
            {stats.uniqueCount.toLocaleString()}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {canonicalAccounts.length} account
            {canonicalAccounts.length !== 1 ? "s" : ""}
            {" · "}
            <span className="text-red-600 dark:text-red-400">
              -{fmtCurrency(stats.totalDebits)}
            </span>
            {" · "}
            <span className="text-emerald-600 dark:text-emerald-400">
              +{fmtCurrency(stats.totalCredits)}
            </span>
            {stats.duplicateCount > 0 && (
              <span className="ml-2 text-amber-600 dark:text-amber-400">
                ({stats.duplicateCount} duplicate
                {stats.duplicateCount !== 1 ? "s" : ""} detected)
              </span>
            )}
          </p>
          {balanceComputation.gaps.length > 0 && (
            <div className="mt-2 rounded-lg border border-amber-300/60 bg-amber-50/60 px-3 py-2 dark:border-amber-700/50 dark:bg-amber-950/20">
              <p className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                The running balance doesn't reconcile with the statements' own
                balances at {balanceComputation.gaps.length.toLocaleString()}{" "}
                point{balanceComputation.gaps.length !== 1 ? "s" : ""} — bank
                statements appear to be missing. Follow up on:
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-amber-700/90 dark:text-amber-400/90">
                {balanceComputation.gaps.slice(0, 4).map((g) => (
                  <li key={g.id} className="tabular-nums">
                    {accountKeyLabel(g.accountKey)}:{" "}
                    {fmtSignedCurrency(g.difference)} of unexplained movement
                    before {formatDate(g.sortableDate)}
                  </li>
                ))}
                {balanceComputation.gaps.length > 4 && (
                  <li>
                    +{(balanceComputation.gaps.length - 4).toLocaleString()}{" "}
                    more — look for the amber markers in the Balance column
                  </li>
                )}
              </ul>
            </div>
          )}
          {ledgerConfigured && ledgerParsing && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Parsing ledger…
            </p>
          )}
          {ledgerConfigured && !ledgerParsing && !vouchingActive && (
            <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
              Couldn't read any ledger rows from the selected document(s).
              Vouching supports CSV / XLSX exports — check the file or pick
              another.
            </p>
          )}
          {vouchingActive && (
            <p className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
                ✓ {vouchSummary.vouched.toLocaleString()} vouched
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="tabular-nums text-amber-600 dark:text-amber-400">
                {vouchSummary.bankOnly.toLocaleString()} bank only
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="tabular-nums text-amber-600 dark:text-amber-400">
                {vouchSummary.ledgerOnly.toLocaleString()} ledger only
              </span>
              <span className="text-muted-foreground">
                ({ledgerRows.length.toLocaleString()} ledger rows)
              </span>
              {isLoadingAll && (
                <span className="text-muted-foreground">
                  (provisional — still loading)
                </span>
              )}
            </p>
          )}
        </div>

        {/* Filter toolbar — two rows so nothing is cramped */}
        <div className="flex flex-col gap-2 border-t border-border px-4 py-3">
          {/* Row 1: search, dates, amounts, group by, duplicates toggle */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] max-w-[280px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setVisibleCount(PAGE_SIZE);
                }}
                className={cn("w-full pl-8 pr-3", TOOLBAR_INPUT)}
              />
            </div>

            <DatePicker
              value={dateFrom || undefined}
              onChange={(v) => {
                setDateFrom(v);
                setVisibleCount(PAGE_SIZE);
              }}
              placeholder="From"
              className="w-[150px]"
              inputClassName="h-8 text-xs rounded-lg py-0"
            />
            <DatePicker
              value={dateTo || undefined}
              onChange={(v) => {
                setDateTo(v);
                setVisibleCount(PAGE_SIZE);
              }}
              placeholder="To"
              className="w-[150px]"
              inputClassName="h-8 text-xs rounded-lg py-0"
            />

            <input
              type="number"
              placeholder="Min $"
              value={amountMin}
              onChange={(e) => {
                setAmountMin(e.target.value);
                setVisibleCount(PAGE_SIZE);
              }}
              className={cn("w-[90px] px-2", TOOLBAR_INPUT, "tabular-nums")}
            />
            <input
              type="number"
              placeholder="Max $"
              value={amountMax}
              onChange={(e) => {
                setAmountMax(e.target.value);
                setVisibleCount(PAGE_SIZE);
              }}
              className={cn("w-[90px] px-2", TOOLBAR_INPUT, "tabular-nums")}
            />

            <Select
              value={groupBy}
              onValueChange={(v) => {
                setGroupBy(v as GroupByKey);
                setVisibleCount(PAGE_SIZE);
              }}
            >
              <SelectTrigger className="h-8 w-[150px] rounded-lg text-xs">
                <SelectValue placeholder="Group by..." />
              </SelectTrigger>
              <SelectContent>
                {GROUP_BY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <ColumnsMenu
              columns={TXN_COLUMNS}
              hidden={hiddenCols}
              onToggle={toggleCol}
            />

            <Button
              variant={hideDuplicates ? "secondary" : "outline"}
              size="sm"
              className="h-8 gap-1.5 rounded-lg text-xs"
              onClick={() => {
                setHideDuplicates(!hideDuplicates);
                setVisibleCount(PAGE_SIZE);
              }}
            >
              {hideDuplicates ? (
                <EyeOff className="h-3 w-3" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
              {hideDuplicates ? "Duplicates hidden" : "Showing all"}
            </Button>

            {!isViewer && (
              <Button
                size="sm"
                className="h-8 gap-1.5 rounded-lg text-xs"
                onClick={() => setVouchModalOpen(true)}
                title="Match transactions against a ledger / Xero export"
              >
                <CheckCircle2 className="h-3 w-3" />
                Vouch
              </Button>
            )}

            {hasFilters && (
              <button
                onClick={() => {
                  setSearch("");
                  setDateFrom("");
                  setDateTo("");
                  setDirectionFilter("all");
                  setAccountFilter([]);
                  setCategoryFilter([]);
                  setCounterpartyFilter([]);
                  setDocumentFilter([]);
                  setAmountMin("");
                  setAmountMax("");
                  setVouchFilter("all");
                }}
                className="ml-auto text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Clear
              </button>
            )}
          </div>

          {/* Row 2: dimension filters — direction, account, category, counterparty, document */}
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={directionFilter}
              onValueChange={(v) => {
                setDirectionFilter(v as "all" | "credit" | "debit");
                setVisibleCount(PAGE_SIZE);
              }}
            >
              <SelectTrigger className="h-8 w-[120px] rounded-lg text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="credit">Credits</SelectItem>
                <SelectItem value="debit">Debits</SelectItem>
              </SelectContent>
            </Select>

            <MultiSearchableSelect
              className="w-[180px]"
              value={accountFilter}
              onValueChange={(v) => {
                setAccountFilter(v);
                setVisibleCount(PAGE_SIZE);
              }}
              options={accountOptions.map((o) => ({
                value: o.value,
                label: o.label,
                title: o.fullLabel,
              }))}
              placeholder="Accounts"
              searchPlaceholder="Search accounts..."
            />

            <MultiSearchableSelect
              className="w-[180px]"
              value={categoryFilter}
              onValueChange={(v) => {
                setCategoryFilter(v);
                setVisibleCount(PAGE_SIZE);
              }}
              options={categoryOptions}
              placeholder="Categories"
              searchPlaceholder="Search categories..."
            />

            <MultiSearchableSelect
              className="w-[200px]"
              value={counterpartyFilter}
              onValueChange={(v) => {
                setCounterpartyFilter(v);
                setVisibleCount(PAGE_SIZE);
              }}
              options={counterpartyOptions}
              placeholder="Counterparties"
              searchPlaceholder="Search counterparties..."
            />

            <MultiSearchableSelect
              className="w-[220px]"
              value={documentFilter}
              onValueChange={(v) => {
                setDocumentFilter(v);
                setVisibleCount(PAGE_SIZE);
              }}
              options={documentOptions}
              placeholder="Documents"
              searchPlaceholder="Search documents..."
              contentClassName="w-[360px]"
            />

            {vouchingActive && (
              <Select
                value={vouchFilter}
                onValueChange={(v) => {
                  setVouchFilter(v as "all" | VouchStatus);
                  setVisibleCount(PAGE_SIZE);
                }}
              >
                <SelectTrigger className="h-8 w-[160px] rounded-lg text-xs">
                  <SelectValue placeholder="Vouch status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vouch states</SelectItem>
                  <SelectItem value="vouched">Vouched</SelectItem>
                  <SelectItem value="bank_only">
                    Bank only (not in ledger)
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between border-t border-border px-5 py-2 text-xs text-muted-foreground">
          <span>
            {groups
              ? `${groups.length.toLocaleString()} group${groups.length !== 1 ? "s" : ""} · ${filtered.length.toLocaleString()} transactions`
              : `Showing ${Math.min(visibleCount, sorted.length).toLocaleString()} of ${sorted.length.toLocaleString()} transactions`}
            {!hideDuplicates && stats.duplicateCount > 0 && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                (includes {stats.duplicateCount} duplicates)
              </span>
            )}
          </span>
          {isLoadingAll && (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading remaining transactions… ({loadedCount.toLocaleString()}{" "}
              loaded)
            </span>
          )}
        </div>

        {/* Grouped view */}
        {groups ? (
          <div className="flex flex-col gap-2 border-t border-border p-3">
            <TooltipProvider delayDuration={300}>
              {groups.map((g) => (
                <CollapsibleGroup
                  key={g.key}
                  label={g.label}
                  count={g.txns.length}
                  totalCredits={g.totalCredits}
                  totalDebits={g.totalDebits}
                  txns={g.txns}
                  groupBy={groupBy}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  onSelectTxn={setSelectedTxn}
                  expandedDupId={expandedDupId}
                  onToggleDup={(id) =>
                    setExpandedDupId(expandedDupId === id ? null : id)
                  }
                  allTransactions={transactions}
                  hidden={hiddenCols}
                />
              ))}
            </TooltipProvider>
          </div>
        ) : (
          <>
            {/* Flat table */}
            <div className="overflow-x-auto border-t border-border">
              <TooltipProvider delayDuration={300}>
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-muted/40">
                    <tr className="text-xs text-muted-foreground">
                      {!hiddenCols.has("date") && (
                        <SortableHeader
                          field="date"
                          label="Date"
                          sortField={sortField}
                          sortDir={sortDir}
                          onSort={handleSort}
                        />
                      )}
                      {!hiddenCols.has("account") && (
                        <SortableHeader
                          field="account"
                          label="Account"
                          sortField={sortField}
                          sortDir={sortDir}
                          onSort={handleSort}
                        />
                      )}
                      {!hiddenCols.has("description") && (
                        <th className="px-3 py-2 text-left font-medium">
                          Description
                        </th>
                      )}
                      {!hiddenCols.has("counterparty") && (
                        <th className="px-3 py-2 text-left font-medium">
                          Counterparty
                        </th>
                      )}
                      {!hiddenCols.has("category") && (
                        <th className="px-3 py-2 text-left font-medium">
                          Category
                        </th>
                      )}
                      {!hiddenCols.has("debit") && (
                        <SortableHeader
                          field="amount"
                          label="Debit"
                          sortField={sortField}
                          sortDir={sortDir}
                          onSort={handleSort}
                          className="text-right"
                        />
                      )}
                      {!hiddenCols.has("credit") && (
                        <th className="px-3 py-2 text-right font-medium">
                          Credit
                        </th>
                      )}
                      {!hiddenCols.has("balance") && (
                        <th className="px-3 py-2 text-right font-medium">
                          Balance
                        </th>
                      )}
                      {vouchingActive && (
                        <th className="px-3 py-2 text-left font-medium">
                          Vouch
                        </th>
                      )}
                      <th className="w-8 px-3 py-2 text-center font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((txn) => (
                      <TxnRow
                        key={txn._id}
                        txn={txn}
                        isExpanded={expandedDupId === txn._id}
                        onToggleDup={() =>
                          setExpandedDupId(
                            expandedDupId === txn._id ? null : txn._id,
                          )
                        }
                        onSelect={() => setSelectedTxn(txn)}
                        allTransactions={transactions}
                        vouchingActive={vouchingActive}
                        hidden={hiddenCols}
                      />
                    ))}
                  </tbody>
                </table>
              </TooltipProvider>
            </div>

            {/* Load more */}
            {hasMore && (
              <button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="w-full border-t border-border py-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
              >
                Show more ({(sorted.length - visibleCount).toLocaleString()}{" "}
                remaining)
              </button>
            )}
          </>
        )}
      </div>

      {/* Transaction detail sheet */}
      {selectedTxn && (
        <TransactionDetailSheet
          txn={selectedTxn}
          jobId={jobId}
          onClose={() => setSelectedTxn(null)}
          isViewer={isViewer}
          vouchingActive={vouchingActive}
        />
      )}

      {/* Vouch — choose the ledger / Xero document from the library */}
      <VouchLedgerPicker
        open={vouchModalOpen}
        onClose={() => setVouchModalOpen(false)}
        jobId={jobId}
        taskId={taskId}
        selectedIds={ledgerDocIds}
        perDoc={ledgerPerDoc}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SortableHeader({
  field,
  label,
  sortField,
  sortDir,
  onSort,
  className,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
  className?: string;
}) {
  const isActive = sortField === field;
  return (
    <th
      className={cn(
        "cursor-pointer select-none px-3 py-2 font-medium transition-colors hover:text-foreground",
        className,
      )}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          sortDir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  );
}

function TxnRow({
  txn,
  isExpanded,
  onToggleDup,
  onSelect,
  allTransactions,
  vouchingActive,
  hidden,
}: {
  txn: BankTxnEntry;
  isExpanded: boolean;
  onToggleDup: () => void;
  onSelect: () => void;
  allTransactions: BankTxnEntry[];
  vouchingActive?: boolean;
  hidden?: Set<string>;
}) {
  const isDup = txn.isDuplicate === true;
  const counterparty = txn.counterpartyName || txn.extractionCounterpartyName;
  const isHidden = (key: string) => hidden?.has(key) ?? false;
  // Base data columns visible, plus the conditional Vouch column and the
  // always-present duplicate/expand action column — used for the expanded
  // duplicate detail row's colSpan.
  const baseVisible = TXN_COLUMNS.filter((c) => !isHidden(c.key)).length;
  const expandedColSpan = baseVisible + (vouchingActive ? 1 : 0) + 1;

  // Find how many duplicates share this fingerprint
  const dupCount = useMemo(() => {
    if (!txn.fingerprint) return 0;
    return allTransactions.filter(
      (t) => t.fingerprint === txn.fingerprint && t._id !== txn._id,
    ).length;
  }, [txn.fingerprint, txn._id, allTransactions]);

  // Find duplicate siblings for the expanded view
  const dupSiblings = useMemo(() => {
    if (!isExpanded || !txn.fingerprint) return [];
    return allTransactions.filter(
      (t) => t.fingerprint === txn.fingerprint && t._id !== txn._id,
    );
  }, [isExpanded, txn.fingerprint, txn._id, allTransactions]);

  return (
    <>
      <tr
        className={cn(
          "cursor-pointer border-t transition-colors hover:bg-muted/30",
          isDup && "bg-amber-50/30 opacity-50 dark:bg-amber-950/10",
        )}
        onClick={onSelect}
      >
        {!isHidden("date") && (
          <td className="whitespace-nowrap px-3 py-1.5 text-xs tabular-nums">
            {formatDate(txn.date)}
          </td>
        )}
        {!isHidden("account") && (
          <td
            className="whitespace-nowrap px-3 py-1.5 text-xs"
            title={formatAccountFull(txn.accountInstitution, txn.accountNumber)}
          >
            <span className="text-muted-foreground">
              {formatAccountShort(txn.accountInstitution, txn.accountNumber)}
            </span>
          </td>
        )}
        {!isHidden("description") && (
          <td className="max-w-[300px] px-3 py-1.5 text-xs">
            <div className="truncate" title={txn.description}>
              {txn.description}
            </div>
          </td>
        )}
        {!isHidden("counterparty") && (
          <td className="max-w-[200px] px-3 py-1.5">
            <CounterpartyBadge name={counterparty} />
          </td>
        )}
        {!isHidden("category") && (
          <td className="px-3 py-1.5">
            <TypeBadge type={txn.transactionType} />
          </td>
        )}
        {!isHidden("debit") && (
          <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-red-600 dark:text-red-400">
            {txn.direction === "debit" ? fmtCurrency(txn.amount) : ""}
          </td>
        )}
        {!isHidden("credit") && (
          <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-emerald-600 dark:text-emerald-400">
            {txn.direction === "credit" ? fmtCurrency(txn.amount) : ""}
          </td>
        )}
        {!isHidden("balance") && (
          <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums">
            <span className="inline-flex items-center gap-1.5">
              {txn.balanceGap && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[300px] text-xs">
                    The statement states a balance of{" "}
                    {fmtBalance(txn.balanceGap.stated)} here, but the
                    transactions on file compute to{" "}
                    {fmtBalance(txn.balanceGap.expected)} —{" "}
                    {fmtSignedCurrency(txn.balanceGap.difference)} is
                    unaccounted for. Bank statements before this date appear to
                    be missing.
                  </TooltipContent>
                </Tooltip>
              )}
              {txn.runningBalance != null ? (
                <span
                  className={
                    txn.runningBalance >= 0
                      ? "text-foreground"
                      : "text-red-600 dark:text-red-400"
                  }
                >
                  {fmtBalance(txn.runningBalance)}
                </span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </span>
          </td>
        )}
        {vouchingActive && (
          <td className="px-3 py-1.5">
            <VouchBadge status={txn.vouchStatus} />
          </td>
        )}
        <td className="px-3 py-1.5 text-center">
          {(isDup || dupCount > 0) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDup();
                  }}
                  className={cn(
                    "inline-flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-muted",
                    isDup ? "text-amber-500" : "text-muted-foreground",
                  )}
                >
                  <Copy className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                {isDup
                  ? "This is a duplicate transaction"
                  : `${dupCount} duplicate${dupCount > 1 ? "s" : ""} detected`}
              </TooltipContent>
            </Tooltip>
          )}
        </td>
      </tr>
      {/* Expanded duplicate detail */}
      {isExpanded && dupSiblings.length > 0 && (
        <tr>
          <td
            colSpan={expandedColSpan}
            className="border-t bg-muted/20 px-6 py-3"
          >
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                {isDup
                  ? "This transaction is a duplicate of:"
                  : `${dupSiblings.length} duplicate(s) from other documents:`}
              </div>
              <div className="space-y-1">
                {dupSiblings.map((sib) => (
                  <div
                    key={sib._id}
                    className="flex items-center gap-4 rounded border bg-background px-3 py-1.5 text-xs"
                  >
                    <span className="tabular-nums text-muted-foreground">
                      {formatDate(sib.date)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {sib.description}
                    </span>
                    <span className="font-mono tabular-nums">
                      {sib.direction === "debit" ? "-" : "+"}
                      {fmtCurrency(sib.amount)}
                    </span>
                    <span
                      className="min-w-0 flex-[2] truncate text-right text-muted-foreground"
                      title={sib.sourceDocumentName ?? sib.sourceFileName}
                    >
                      Source:{" "}
                      {sib.sourceDocumentName ??
                        sib.sourceFileName ??
                        "Unknown document"}
                    </span>
                    {sib.isDuplicate && (
                      <Badge
                        variant="outline"
                        className="border-amber-300 text-[9px] text-amber-600"
                      >
                        Duplicate
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Grouping helpers + CollapsibleGroup
// ---------------------------------------------------------------------------

function getGroupKey(txn: BankTxnEntry, groupBy: GroupByKey): string {
  const isAgentOrUser = txn.typeSource === "agent" || txn.typeSource === "user";
  switch (groupBy) {
    case "direction":
      return txn.direction;
    case "counterparty": {
      const best = isAgentOrUser
        ? (txn.counterpartyName ?? txn.extractionCounterpartyName)
        : (txn.extractionCounterpartyName ?? txn.counterpartyName);
      return (best ?? "Uncategorised").trim() || "Uncategorised";
    }
    case "category":
      return txn.transactionType;
    case "account":
      return (
        accountKey(txn.accountInstitution, txn.accountNumber) || "__unknown__"
      );
    default:
      return "";
  }
}

function getGroupLabel(key: string, groupBy: GroupByKey): string {
  if (groupBy === "category") return TYPE_LABELS[key] ?? key;
  if (groupBy === "direction")
    return key === "credit" ? "Credits" : key === "debit" ? "Debits" : key;
  if (groupBy === "account") {
    if (key === "__unknown__") return "Unknown account";
    const [inst, num] = key.split(":");
    return formatAccountShort(inst, num) || "Unknown account";
  }
  return key;
}

const fmtShort = (n: number): string =>
  "$" +
  Math.abs(n).toLocaleString("en-AU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

function CollapsibleGroup({
  label,
  count,
  totalCredits,
  totalDebits,
  txns,
  groupBy,
  sortField,
  sortDir,
  onSort,
  onSelectTxn,
  expandedDupId,
  onToggleDup,
  allTransactions,
  hidden,
}: {
  label: string;
  count: number;
  totalCredits: number;
  totalDebits: number;
  txns: BankTxnEntry[];
  groupBy: GroupByKey;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
  onSelectTxn: (txn: BankTxnEntry) => void;
  expandedDupId: string | null;
  onToggleDup: (id: string) => void;
  allTransactions: BankTxnEntry[];
  hidden?: Set<string>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const sorted = useMemo(() => {
    const arr = [...txns];
    if (!sortField) return arr;
    arr.sort((a, b) => {
      const cmp = ((): number => {
        switch (sortField) {
          case "date":
            return toSortableDate(a.date).localeCompare(toSortableDate(b.date));
          case "amount":
            return a.amount - b.amount;
          case "account":
            return (a.accountInstitution ?? "").localeCompare(
              b.accountInstitution ?? "",
            );
          default:
            return 0;
        }
      })();
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [txns, sortField, sortDir]);

  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;
  const net = totalCredits - totalDebits;

  // Derived dominant badges for non-counterparty / non-category groupings —
  // mirrors the Documents panel and adds context to the row.
  const { dominantCategory, dominantCounterparty } = useMemo(() => {
    const cat = new Map<string, number>();
    const cp = new Map<string, number>();
    for (const t of txns) {
      cat.set(t.transactionType, (cat.get(t.transactionType) ?? 0) + 1);
      const isAgentOrUser = t.typeSource === "agent" || t.typeSource === "user";
      const c = isAgentOrUser
        ? (t.counterpartyName ?? t.extractionCounterpartyName)
        : (t.extractionCounterpartyName ?? t.counterpartyName);
      if (c) cp.set(c, (cp.get(c) ?? 0) + 1);
    }
    return {
      dominantCategory: [...cat.entries()].sort((a, b) => b[1] - a[1])[0]?.[0],
      dominantCounterparty: [...cp.entries()].sort(
        (a, b) => b[1] - a[1],
      )[0]?.[0],
    };
  }, [txns]);

  return (
    <div className="overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-muted/30"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
              isOpen && "rotate-90",
            )}
          />
          <span className="truncate text-sm font-medium">{label}</span>
          {groupBy !== "category" && dominantCategory && (
            <TypeBadge type={dominantCategory} />
          )}
          {groupBy !== "counterparty" && dominantCounterparty && (
            <CounterpartyBadge name={dominantCounterparty} />
          )}
        </div>
        <div className="ml-4 flex shrink-0 items-center gap-2">
          <Badge variant="soft" size="sm" color="gray" className="tabular-nums">
            {count.toLocaleString()} txn{count !== 1 ? "s" : ""}
          </Badge>
          <Badge
            variant="soft"
            size="sm"
            color="green"
            className="gap-1 tabular-nums"
          >
            Credits{" "}
            <span className="font-semibold">+{fmtShort(totalCredits)}</span>
          </Badge>
          <Badge
            variant="soft"
            size="sm"
            color="red"
            className="gap-1 tabular-nums"
          >
            Debits{" "}
            <span className="font-semibold">-{fmtShort(totalDebits)}</span>
          </Badge>
          <Badge
            variant="soft"
            size="sm"
            color={net >= 0 ? "green" : "red"}
            className="gap-1 tabular-nums"
          >
            Net{" "}
            <span className="font-semibold">
              {net >= 0 ? "+" : "-"}
              {fmtShort(net)}
            </span>
          </Badge>
        </div>
      </button>

      {isOpen && (
        <div className="border-t">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted/40">
                <tr className="text-xs text-muted-foreground">
                  {!hidden?.has("date") && (
                    <SortableHeader
                      field="date"
                      label="Date"
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                  )}
                  {!hidden?.has("account") && (
                    <SortableHeader
                      field="account"
                      label="Account"
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={onSort}
                    />
                  )}
                  {!hidden?.has("description") && (
                    <th className="px-3 py-2 text-left font-medium">
                      Description
                    </th>
                  )}
                  {!hidden?.has("counterparty") && (
                    <th className="px-3 py-2 text-left font-medium">
                      Counterparty
                    </th>
                  )}
                  {!hidden?.has("category") && (
                    <th className="px-3 py-2 text-left font-medium">
                      Category
                    </th>
                  )}
                  {!hidden?.has("debit") && (
                    <SortableHeader
                      field="amount"
                      label="Debit"
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={onSort}
                      className="text-right"
                    />
                  )}
                  {!hidden?.has("credit") && (
                    <th className="px-3 py-2 text-right font-medium">Credit</th>
                  )}
                  {!hidden?.has("balance") && (
                    <th className="px-3 py-2 text-right font-medium">
                      Balance
                    </th>
                  )}
                  <th className="w-8 px-3 py-2 text-center font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((txn) => (
                  <TxnRow
                    key={txn._id}
                    txn={txn}
                    isExpanded={expandedDupId === txn._id}
                    onToggleDup={() => onToggleDup(txn._id)}
                    onSelect={() => onSelectTxn(txn)}
                    allTransactions={allTransactions}
                    hidden={hidden}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <button
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="w-full border-t py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
            >
              Show more ({(sorted.length - visibleCount).toLocaleString()}{" "}
              remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
